import React, { useState, useEffect } from 'react';
import {
  Search,
  Bell,
  CheckCircle2,
  AlertTriangle,
  Activity,
  Layers,
  Lock,
  Network,
  FileText,
  RefreshCw,
  Users,
  TrendingUp,
  DollarSign,
  Database,
  ShieldAlert,
  Sliders,
  HelpCircle,
  LogOut,
  ChevronRight,
  Plus,
  UserCheck,
  Server,
  ShieldCheck,
  Check,
  ExternalLink,
  Clock,
  Filter,
  X
} from 'lucide-react';
import { AppView } from '../../types';

interface SystemOverviewProps {
  onNavigateToView: (view: AppView) => void;
  onOpenSearch?: () => void;
}

interface CoreService {
  id: string;
  name: string;
  status: 'Operational' | 'Degraded' | 'Maintenance' | 'Down';
  latency: string;
  load: string;
  description: string;
}

interface SystemEvent {
  id: string;
  timestamp: string;
  title: string;
  severity: 'INFO' | 'WARNING' | 'CRITICAL';
  service: string;
  details: string;
}

export const SystemOverview: React.FC<SystemOverviewProps> = ({
  onNavigateToView,
  onOpenSearch
}) => {
  const [lastUpdated, setLastUpdated] = useState<string>('Just now');
  const [selectedDomain, setSelectedDomain] = useState<string>('operations');
  const [activeSubTab, setActiveSubTab] = useState<'system-overview' | 'security' | 'incidents'>('system-overview');
  const [showAllServices, setShowAllServices] = useState<boolean>(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [showNotificationsPopover, setShowNotificationsPopover] = useState<boolean>(false);
  const [showProfilePopover, setShowProfilePopover] = useState<boolean>(false);

  // Real Service Catalog matching strict requirements
  const [services] = useState<CoreService[]>([
    { id: 'web-app', name: 'Web Application', status: 'Operational', latency: '-', load: '-', description: 'React/Vite SPA & Cloud Run ingress' },
    { id: 'public-api', name: 'Public API', status: 'Operational', latency: '-', load: '-', description: 'Gateway REST/GraphQL endpoints' },
    { id: 'auth', name: 'Authentication', status: 'Operational', latency: '-', load: '-', description: 'OIDC, Passkeys & Session Engine' },
    { id: 'postgresql', name: 'PostgreSQL', status: 'Operational', latency: '-', load: '-', description: 'Primary relational DB cluster' },
    { id: 'redis-queue', name: 'Redis / Queue', status: 'Operational', latency: '-', load: '-', description: 'Pub/Sub & async worker queues' },
    { id: 'workers', name: 'Workers', status: 'Operational', latency: '-', load: '-', description: 'Background job processors' },
    { id: 'fin-ingestion', name: 'Financial Data Ingestion', status: 'Operational', latency: '-', load: '-', description: 'FX, ECB & Central Bank pipelines' },
    { id: 'ai-gateway', name: 'AI Gateway', status: 'Operational', latency: '-', load: '-', description: 'Gemini 2.5 API proxy & rate limiter' },
    { id: 'object-storage', name: 'Object Storage', status: 'Operational', latency: '-', load: '-', description: 'Document & report export bucket' }
  ]);

  // Real operational events (empty by default unless explicitly logged)
  const [systemEvents] = useState<SystemEvent[]>([]);

  const triggerToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 3000);
  };

  const handleRefresh = () => {
    const timeStr = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    setLastUpdated(`at ${timeStr}`);
    triggerToast('System status refreshed.');
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex text-[#1E293B] font-sans antialiased">
      {/* =============================================================== */}
      {/* 1. LEFT SIDEBAR (7 Canonical Domains)                           */}
      {/* =============================================================== */}
      <aside className="w-64 bg-white border-r border-[#E2E8F0] flex flex-col shrink-0 min-h-screen sticky top-0 h-screen overflow-y-auto">
        {/* Brand Header */}
        <div className="p-5 border-b border-[#F1F5F9] flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-[#0F172A] flex items-center justify-center text-white shadow-xs font-black text-lg">
            <ShieldCheck className="w-5 h-5 text-emerald-400" />
          </div>
          <div>
            <h1 className="text-base font-extrabold text-[#0F172A] tracking-tight leading-none">
              Control Center
            </h1>
            <span className="text-[10px] font-bold text-[#64748B] uppercase tracking-wider block mt-1">
              Administrator
            </span>
          </div>
        </div>

        {/* Sidebar Navigation Links */}
        <div className="flex-1 py-4 px-3 space-y-6">
          {/* Section: CORE */}
          <div>
            <div className="px-3 mb-2 text-[10px] font-bold text-[#94A3B8] uppercase tracking-widest">
              CORE
            </div>
            <nav className="space-y-1">
              <button
                onClick={() => {
                  setSelectedDomain('overview');
                  onNavigateToView('admin-super-command-center');
                }}
                className={`w-[#95%] flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-medium transition-all cursor-pointer ${
                  selectedDomain === 'overview'
                    ? 'bg-[#2563EB] text-white font-bold shadow-xs'
                    : 'text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A]'
                }`}
              >
                <Layers className="w-4 h-4" />
                <span>Overview</span>
              </button>

              <button
                onClick={() => {
                  setSelectedDomain('customers');
                  onNavigateToView('admin-organizations');
                }}
                className={`w-[#95%] flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-medium transition-all cursor-pointer ${
                  selectedDomain === 'customers'
                    ? 'bg-[#2563EB] text-white font-bold'
                    : 'text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A]'
                }`}
              >
                <Users className="w-4 h-4" />
                <span>Customers & Support</span>
              </button>

              <button
                onClick={() => {
                  setSelectedDomain('product');
                  onNavigateToView('cpo-command-center');
                }}
                className={`w-[#95%] flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-medium transition-all cursor-pointer ${
                  selectedDomain === 'product'
                    ? 'bg-[#2563EB] text-white font-bold'
                    : 'text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A]'
                }`}
              >
                <TrendingUp className="w-4 h-4" />
                <span>Product & Growth</span>
              </button>
            </nav>
          </div>

          {/* Section: PERFORMANCE */}
          <div>
            <div className="px-3 mb-2 text-[10px] font-bold text-[#94A3B8] uppercase tracking-widest">
              PERFORMANCE
            </div>
            <nav className="space-y-1">
              <button
                onClick={() => {
                  setSelectedDomain('revenue');
                  onNavigateToView('cfo-command-center');
                }}
                className={`w-[#95%] flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-medium transition-all cursor-pointer ${
                  selectedDomain === 'revenue'
                    ? 'bg-[#2563EB] text-white font-bold'
                    : 'text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A]'
                }`}
              >
                <DollarSign className="w-4 h-4" />
                <span>Revenue & Finance</span>
              </button>

              <button
                onClick={() => {
                  setSelectedDomain('data');
                  onNavigateToView('admin-ai-platform');
                }}
                className={`w-[#95%] flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-medium transition-all cursor-pointer ${
                  selectedDomain === 'data'
                    ? 'bg-[#2563EB] text-white font-bold'
                    : 'text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A]'
                }`}
              >
                <Database className="w-4 h-4" />
                <span>Data & AI</span>
              </button>
            </nav>
          </div>

          {/* Section: SYSTEM */}
          <div>
            <div className="px-3 mb-2 text-[10px] font-bold text-[#94A3B8] uppercase tracking-widest">
              SYSTEM
            </div>
            <nav className="space-y-1">
              <button
                onClick={() => {
                  setSelectedDomain('operations');
                }}
                className={`w-[#95%] flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                  selectedDomain === 'operations'
                    ? 'bg-[#2563EB] text-white shadow-xs'
                    : 'text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A]'
                }`}
              >
                <ShieldAlert className="w-4 h-4" />
                <span>Operations & Risk</span>
              </button>

              {/* Sub-navigation under Operations & Risk */}
              {selectedDomain === 'operations' && (
                <div className="ml-4 pl-3 border-l-2 border-[#2563EB] my-1 space-y-1">
                  <button
                    onClick={() => setActiveSubTab('system-overview')}
                    className={`w-full text-left px-2 py-1.5 rounded-lg text-xs font-bold transition-colors cursor-pointer ${
                      activeSubTab === 'system-overview'
                        ? 'text-[#2563EB] bg-blue-50'
                        : 'text-[#64748B] hover:text-[#0F172A]'
                    }`}
                  >
                    System Overview
                  </button>
                  <button
                    onClick={() => {
                      onNavigateToView('admin-compliance');
                    }}
                    className="w-full text-left px-2 py-1.5 rounded-lg text-xs font-medium text-[#64748B] hover:text-[#0F172A] transition-colors cursor-pointer"
                  >
                    Compliance Monitor
                  </button>
                  <button
                    onClick={() => {
                      onNavigateToView('admin-system-health');
                    }}
                    className="w-full text-left px-2 py-1.5 rounded-lg text-xs font-medium text-[#64748B] hover:text-[#0F172A] transition-colors cursor-pointer"
                  >
                    Health & Deployments
                  </button>
                  <button
                    onClick={() => {
                      setActiveSubTab('security');
                      onNavigateToView('ciso-security-command-center');
                    }}
                    className="w-full text-left px-2 py-1.5 rounded-lg text-xs font-medium text-[#64748B] hover:text-[#0F172A] transition-colors cursor-pointer"
                  >
                    Security & Compliance
                  </button>
                </div>
              )}

              <button
                onClick={() => {
                  setSelectedDomain('configuration');
                  onNavigateToView('admin-roles-permissions');
                }}
                className={`w-[#95%] flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-medium transition-all cursor-pointer ${
                  selectedDomain === 'configuration'
                    ? 'bg-[#2563EB] text-white font-bold'
                    : 'text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A]'
                }`}
              >
                <Sliders className="w-4 h-4" />
                <span>Configuration</span>
              </button>
            </nav>
          </div>
        </div>

        {/* Sidebar Footer Profile */}
        <div className="p-3 border-t border-[#F1F5F9] space-y-2">
          <div className="flex items-center gap-3 p-2 bg-[#F8FAFC] rounded-xl border border-[#E2E8F0]">
            <div className="w-8 h-8 rounded-full bg-[#0F172A] text-emerald-400 font-bold text-xs flex items-center justify-center border border-emerald-500/30">
              AU
            </div>
            <div className="min-w-0 flex-1">
              <span className="text-xs font-bold text-[#0F172A] block truncate">Admin User</span>
              <span className="text-[10px] font-mono text-[#64748B] block truncate">admin@yamoopay.com</span>
            </div>
          </div>
          <p className="text-[10px] text-center text-[#94A3B8] font-mono">
            YamooPay Admin v2.5.1 (Stable)
          </p>
        </div>
      </aside>

      {/* =============================================================== */}
      {/* 2. MAIN CONTENT AREA                                           */}
      {/* =============================================================== */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* TOPBAR */}
        <header className="h-16 bg-white border-b border-[#E2E8F0] px-6 flex items-center justify-between sticky top-0 z-20 shadow-2xs">
          {/* Global Search Button */}
          <div className="flex items-center gap-4 flex-1 max-w-md">
            <button
              onClick={onOpenSearch}
              className="w-full flex items-center justify-between px-3.5 py-2 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl text-xs text-[#64748B] hover:border-[#CBD5E1] transition-all cursor-pointer"
            >
              <div className="flex items-center gap-2">
                <Search className="w-4 h-4 text-[#94A3B8]" />
                <span>Search resources...</span>
              </div>
              <kbd className="px-1.5 py-0.5 bg-white border border-[#CBD5E1] rounded text-[10px] font-mono font-bold text-[#64748B]">
                ⌘K
              </kbd>
            </button>
          </div>

          {/* Topbar Utility Icons */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => triggerToast('System security verified: MFA & TLS Enforced')}
              className="p-2 text-[#64748B] hover:text-[#0F172A] hover:bg-[#F1F5F9] rounded-xl transition-colors cursor-pointer"
              title="Security Status"
            >
              <ShieldCheck className="w-4 h-4 text-emerald-600" />
            </button>
            <button
              onClick={() => onNavigateToView('admin-audit')}
              className="p-2 text-[#64748B] hover:text-[#0F172A] hover:bg-[#F1F5F9] rounded-xl transition-colors cursor-pointer"
              title="Audit Logs"
            >
              <FileText className="w-4 h-4 text-[#64748B]" />
            </button>
            <button
              onClick={() => setShowNotificationsPopover(!showNotificationsPopover)}
              className="p-2 text-[#64748B] hover:text-[#0F172A] hover:bg-[#F1F5F9] rounded-xl transition-colors relative cursor-pointer"
              title="Notifications"
            >
              <Bell className="w-4 h-4" />
            </button>
          </div>
        </header>

        {/* PAGE BODY */}
        <main className="p-8 space-y-8 max-w-7xl mx-auto w-full">
          {/* Toast Notification */}
          {toastMessage && (
            <div className="fixed bottom-6 right-6 z-50 bg-[#0F172A] text-white text-xs font-bold px-4 py-3 rounded-xl shadow-2xl flex items-center gap-2 animate-in slide-in-from-bottom-5 duration-200">
              <CheckCircle2 className="w-4 h-4 text-emerald-400" />
              <span>{toastMessage}</span>
            </div>
          )}

          {/* Title Header with Freshness Indicator */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-black text-[#0F172A] tracking-tight">
                System Overview
              </h1>
              <p className="text-xs font-medium text-[#64748B] mt-1">
                Real-time operational dimensions and system health metrics.
              </p>
            </div>

            {/* Freshness Badge */}
            <div className="flex items-center gap-2">
              <button
                onClick={handleRefresh}
                className="p-1.5 text-[#64748B] hover:text-[#0F172A] hover:bg-[#E2E8F0] rounded-lg transition-colors cursor-pointer"
                title="Refresh system health"
              >
                <RefreshCw className="w-3.5 h-3.5" />
              </button>
              <div className="flex items-center gap-1.5 text-xs text-[#64748B] font-medium">
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                <span>Last updated: <strong className="text-[#0F172A] font-semibold">{lastUpdated}</strong></span>
              </div>
            </div>
          </div>

          {/* =============================================================== */}
          {/* 3. SYNTHETIC HEALTH KPI ROW (5 Cards)                           */}
          {/* =============================================================== */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* Card 1: Critical Incidents */}
            <div className="bg-white border border-[#E2E8F0] rounded-2xl p-4 shadow-2xs hover:border-[#CBD5E1] transition-all space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold text-[#64748B] tracking-wider uppercase">
                  CRITICAL INCIDENTS
                </span>
                <AlertTriangle className="w-4 h-4 text-[#94A3B8]" />
              </div>
              <div className="text-2xl font-black text-[#0F172A] tracking-tight">
                0
              </div>
              <p className="text-[11px] text-[#64748B] font-medium">
                Open across all regions
              </p>
            </div>

            {/* Card 2: Service Availability */}
            <div className="bg-white border border-[#E2E8F0] rounded-2xl p-4 shadow-2xs hover:border-[#CBD5E1] transition-all space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold text-[#64748B] tracking-wider uppercase">
                  SERVICE AVAIL.
                </span>
                <Server className="w-4 h-4 text-[#94A3B8]" />
              </div>
              <div className="text-2xl font-normal italic text-[#94A3B8] tracking-tight">
                No Data
              </div>
              <p className="text-[11px] text-[#64748B] font-medium">
                No production telemetry yet.
              </p>
            </div>

            {/* Card 3: API Error Rate */}
            <div className="bg-white border border-[#E2E8F0] rounded-2xl p-4 shadow-2xs hover:border-[#CBD5E1] transition-all space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold text-[#64748B] tracking-wider uppercase">
                  API ERROR RATE
                </span>
                <Activity className="w-4 h-4 text-[#94A3B8]" />
              </div>
              <div className="text-2xl font-normal italic text-[#94A3B8] tracking-tight">
                No Data
              </div>
              <p className="text-[11px] text-[#64748B] font-medium">
                No production telemetry yet.
              </p>
            </div>

            {/* Card 4: Auth Anomalies */}
            <div className="bg-white border border-[#E2E8F0] rounded-2xl p-4 shadow-2xs hover:border-[#CBD5E1] transition-all space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold text-[#64748B] tracking-wider uppercase">
                  AUTH ANOMALIES
                </span>
                <Lock className="w-4 h-4 text-[#94A3B8]" />
              </div>
              <div className="text-2xl font-black text-[#0F172A] tracking-tight">
                None
              </div>
              <p className="text-[11px] text-[#64748B] font-medium">
                Active threat detection
              </p>
            </div>

            {/* Card 5: Provider Status */}
            <div className="bg-white border border-[#E2E8F0] rounded-2xl p-4 shadow-2xs hover:border-[#CBD5E1] transition-all space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold text-[#64748B] tracking-wider uppercase">
                  PROVIDER STATUS
                </span>
                <Network className="w-4 h-4 text-[#94A3B8]" />
              </div>
              <div className="text-2xl font-black text-[#0F172A] tracking-tight flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 shrink-0" />
                <span>Normal</span>
              </div>
              <p className="text-[11px] text-[#64748B] font-medium">
                Upstream partners
              </p>
            </div>
          </div>

          {/* =============================================================== */}
          {/* 4. MAIN TWO-COLUMN LAYOUT                                      */}
          {/* =============================================================== */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* LEFT COLUMN (7 cols): Core Services Catalog */}
            <div className="lg:col-span-7 bg-white border border-[#E2E8F0] rounded-2xl p-6 shadow-2xs space-y-5">
              <div className="flex items-center justify-between border-b border-[#F1F5F9] pb-4">
                <h3 className="text-base font-extrabold text-[#0F172A]">
                  Core Services Catalog
                </h3>
                <button
                  onClick={() => setShowAllServices(!showAllServices)}
                  className="text-xs font-bold text-[#2563EB] hover:text-[#1D4ED8] cursor-pointer"
                >
                  {showAllServices ? 'Show Less' : 'View All'}
                </button>
              </div>

              {/* Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead>
                    <tr className="border-b border-[#F1F5F9] text-[#64748B]">
                      <th className="pb-3 font-semibold w-1/2">Service</th>
                      <th className="pb-3 font-semibold">Status</th>
                      <th className="pb-3 font-semibold text-center">Latency (ms)</th>
                      <th className="pb-3 font-semibold text-right">Load</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#F1F5F9]">
                    {services.map((srv) => (
                      <tr key={srv.id} className="hover:bg-[#F8FAFC] transition-colors">
                        <td className="py-3.5 pr-4">
                          <span className="font-extrabold text-[#0F172A] block">{srv.name}</span>
                          <span className="text-[11px] text-[#94A3B8]">{srv.description}</span>
                        </td>
                        <td className="py-3.5">
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-emerald-50 text-emerald-700 text-xs font-bold rounded-lg border border-emerald-200">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                            Operational
                          </span>
                        </td>
                        <td className="py-3.5 text-center text-[#94A3B8] font-mono">{srv.latency}</td>
                        <td className="py-3.5 text-right text-[#94A3B8] font-mono">{srv.load}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* RIGHT COLUMN (5 cols): Recent System Events */}
            <div className="lg:col-span-5 bg-white border border-[#E2E8F0] rounded-2xl p-6 shadow-2xs space-y-6 flex flex-col justify-between">
              <div>
                <div className="border-b border-[#F1F5F9] pb-4">
                  <h3 className="text-base font-extrabold text-[#0F172A]">
                    Recent System Events
                  </h3>
                </div>

                {systemEvents.length > 0 ? (
                  <div className="space-y-3 pt-4">
                    {systemEvents.map((evt) => (
                      <div
                        key={evt.id}
                        className="p-3.5 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl space-y-1"
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-bold text-[#0F172A]">{evt.title}</span>
                          <span className="text-[10px] font-mono text-[#94A3B8]">{evt.timestamp}</span>
                        </div>
                        <p className="text-[11px] text-[#64748B]">{evt.details}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="py-16 px-4 text-center space-y-3">
                    <div className="w-12 h-12 rounded-2xl bg-[#F1F5F9] text-[#94A3B8] flex items-center justify-center mx-auto">
                      <FileText className="w-6 h-6" />
                    </div>
                    <p className="text-xs font-medium text-[#64748B] max-w-xs mx-auto leading-relaxed">
                      System operating normally. No significant operational events logged in the last 24 hours.
                    </p>
                  </div>
                )}
              </div>

              {/* Status Footer Card */}
              <div className="p-3.5 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl flex items-center justify-between text-xs">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  <span className="font-bold text-[#0F172A]">Telemetry Monitoring Active</span>
                </div>
                <span className="text-[11px] text-[#64748B] font-mono">0 alerts queued</span>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};
